import { DrawerState, EmailCenterDrawerName } from '@/models/emailCenter';
import { LANGUAGE_CODES, languageLabel } from '@/pages/EmailCenter/constants';
import { buildThemePreviewContext } from '@/utils/emailCenter';
import { createSelector } from 'reselect';

const selectEmailCenter = (state: DefaultRootState) => state.emailCenter;

export const selectTransactionalTemplates = createSelector(
  selectEmailCenter,
  (ec) => ec.templates.transactional,
);
export const selectMarketingTemplates = createSelector(
  selectEmailCenter,
  (ec) => ec.templates.marketing,
);
export const selectTemplatesLoaded = createSelector(
  selectEmailCenter,
  (ec) => ec.templates.loaded,
);
/** True when either type has more templates than the one fetched max-size page. */
export const selectTemplatesTruncated = createSelector(
  selectEmailCenter,
  (ec) =>
    ec.templates.transactionalTotal > ec.templates.transactional.length ||
    ec.templates.marketingTotal > ec.templates.marketing.length,
);
export const selectTemplateTypes = createSelector(
  selectEmailCenter,
  (ec) => ec.templateTypes,
);
export const selectVariables = createSelector(
  selectEmailCenter,
  (ec) => ec.variables,
);

/**
 * The namespaces our own render resolves, as `/email-variables` declares them —
 * or undefined when it has not been asked yet.
 *
 * The distinction matters: undefined makes the compiler defer nothing to send
 * time, while an empty array would make it treat every namespace as the
 * provider's. See `renderTimeNamespaces` in the model.
 */
export const selectRenderTimeNamespaces = createSelector(
  selectEmailCenter,
  (ec) => ec.renderTimeNamespaces,
);

export const selectSelectedJourneyHashId = createSelector(
  selectEmailCenter,
  (ec) => ec.selectedJourneyHashId,
);

/**
 * The languages every picker in the Email Center should offer.
 *
 * **An empty preference means every language**, and this is the one place that
 * rule is applied — a consumer reading `partnerLanguages` directly would show a
 * partner who never set the preference *no* languages at all. Everything that
 * renders a language list goes through here.
 */
export const selectTranslationLanguages = createSelector(
  (state: any) => state.emailCenter.partnerLanguages as string[],
  (languages) =>
    languages.length ? languages : (LANGUAGE_CODES as unknown as string[]),
);

/** The same list as `<Select>` options, flagged and uppercased. */
export const selectTranslationLanguageOptions = createSelector(
  selectTranslationLanguages,
  (codes) => codes.map((code) => ({ value: code, label: languageLabel(code) })),
);

/** The whole email-config slice: values, `exists` and which journey it is for. */
export const selectJourneyEmailConfigState = createSelector(
  selectEmailCenter,
  (ec) => ec.journeyEmailConfig,
);
/** Just the stored values — what the sender form and its summary card edit. */
export const selectJourneyEmailConfig = createSelector(
  selectJourneyEmailConfigState,
  (state) => state.config,
);
export const selectJourneyThemeState = createSelector(
  selectEmailCenter,
  (ec) => ec.journeyTheme,
);
/** {{ path }} → value map of the loaded journey theme for preview panes. */
export const selectThemePreviewContext = createSelector(
  selectJourneyThemeState,
  (journeyTheme) => buildThemePreviewContext(journeyTheme.theme),
);
export const selectJourneyTemplates = createSelector(
  selectEmailCenter,
  (ec) => ec.journeyTemplates,
);
export const selectAutomations = createSelector(
  selectEmailCenter,
  (ec) => ec.automations,
);
export const selectMailers = createSelector(
  selectEmailCenter,
  (ec) => ec.mailers,
);
export const selectMailersAccessDenied = createSelector(
  selectEmailCenter,
  (ec) => ec.mailersAccessDenied,
);
export const selectTranslations = createSelector(
  selectEmailCenter,
  (ec) => ec.translations,
);
export const selectFragments = createSelector(
  selectEmailCenter,
  (ec) => ec.fragments,
);
/** `fragment key` → Twig source, the shape the preview renderer registers. */
export const selectFragmentSources = createSelector(selectFragments, (rows) =>
  Object.fromEntries(rows.map((row) => [row.key, row.content])),
);
/**
 * Fragment key → fragment_type, so the block registry can exclude layouts
 * (a layout is a template's frame, not a placeable block).
 */
export const selectFragmentTypes = createSelector(selectFragments, (rows) =>
  Object.fromEntries(rows.map((row) => [row.key, row.type])),
);
/**
 * Fragment key → server-materialized manifest (parsed on save from the
 * `{# @component #}` comment). Null/absent rows fall back to the client-side
 * comment parse in the registry — rows saved before the column, or bare
 * partials with no manifest at all.
 */
export const selectFragmentManifests = createSelector(selectFragments, (rows) =>
  Object.fromEntries(rows.map((row) => [row.key, row.manifest ?? null])),
);
/**
 * Fragment key → the partner's label for it. A partner block's key is opaque, so
 * the block registry needs this to name blocks in the palette and layers tree.
 */
export const selectFragmentNames = createSelector(selectFragments, (rows) =>
  Object.fromEntries(rows.map((row) => [row.key, row.name ?? null])),
);
/**
 * Fragment key → whether the row belongs to this partner (`is_owned`). The
 * template editor offers "edit this building block" only for keys answering
 * true: a non-owned row can only be copied, under a key the open email does not
 * include. See `isFragmentOpenableFromTemplate`.
 */
export const selectFragmentOwnership = createSelector(selectFragments, (rows) =>
  Object.fromEntries(rows.map((row) => [row.key, row.is_owned])),
);
/** Fully-qualified manifest label key → resolved text for the active locale. */
export const selectComponentLabels = createSelector(
  selectEmailCenter,
  (ec) => ec.componentLabels,
);
export const selectComponentLabelsLocale = createSelector(
  selectEmailCenter,
  (ec) => ec.componentLabelsLocale,
);
export const selectEmailSettings = createSelector(
  selectEmailCenter,
  (ec) => ec.emailSettings,
);
export const selectDefaultLayoutKey = createSelector(
  selectEmailSettings,
  (settings) => settings?.default_layout_key ?? null,
);

/**
 * Typed as `(state) => DrawerState` rather than
 * `ReturnType<typeof createSelector>`: that erases the result type to `unknown`,
 * which is why every `drawer.visible` / `drawer.record` in the drawers read as an
 * error. `record` stays `unknown` — each drawer holds a different row and casts
 * it on the way out.
 */
type DrawerSelector = (state: DefaultRootState) => DrawerState;

const drawerSelectorCache = new Map<EmailCenterDrawerName, DrawerSelector>();
export const selectDrawer = (drawer: EmailCenterDrawerName): DrawerSelector => {
  if (!drawerSelectorCache.has(drawer)) {
    drawerSelectorCache.set(
      drawer,
      createSelector(selectEmailCenter, (ec) => ec.drawers[drawer]),
    );
  }
  return drawerSelectorCache.get(drawer)!;
};
